import {Evaluator, registerNode} from "@cm/graph-v2-final"
import {
    AnyImageOpContext,
    AnyImageOpResult,
    ImageOpOutputs,
    ValueOrNode,
} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/image-op-node"
import {definition} from "@app/textures/texture-editor/operator-stack/operators/recolor/node/definition"
import {ImageOpNodeConvertColorSpace} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/convert-color-space/node"
import {ImageOpNodeMath} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/math/node"
import {ImageOpNodeBlend} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/blend/node"
import {ImageOpNodeExtractChannel} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/extract-channel/node"
import {ImageOpNodeCombineChannels} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/combine-channels/node"
import {ImageOpNodeBag} from "@app/textures/texture-editor/operator-stack/image-op-system/helpers/image-op-node-bag"
import {CompositeImageOpNode} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/composite-image-op-node"
import {buildLabSource, buildLiveLabStats} from "@app/textures/texture-editor/operator-stack/operators/recolor/helpers/lab-stats-subgraph"

@registerNode
export class ImageOpNodeOperatorRecolor extends CompositeImageOpNode<typeof definition> {
    static nodeClass = "ImageOpNodeOperatorRecolor"
    static definition = definition

    override async execute(evaluator: Evaluator<AnyImageOpContext>, imageOpNodeBag: ImageOpNodeBag): Promise<ImageOpOutputs<typeof definition>> {
        const {sourceImage, targetStats, sourceStats, luminance, colorIntensity, fade} = await evaluator.getAll(this.parameters)

        const isGrayscale = sourceImage.descriptor.channels === 1

        // statistical color transfer (Reinhard et al.) in Lab space: rescale the source
        // image's per-channel mean/stddev (computed by the operator facade — see
        // OperatorRecolor.createImageOpGraph, cached across reloads) to the user-entered target
        // mean/stddev

        const labSource = buildLabSource(imageOpNodeBag, "source", sourceImage, "linear", isGrayscale)

        // sourceStats is only supplied by the facade for interactive preview (cached across
        // reloads there — see OperatorRecolor.createImageOpGraph); otherwise (baking) it's built
        // live here, folded into whatever evaluation is already running instead of a separate
        // eager evaluate() pass.
        const {mean: sourceMean, stddev: sourceStddev} = sourceStats
            ? {
                  mean: isGrayscale ? sourceStats.l.mean : {r: sourceStats.l.mean, g: sourceStats.a.mean, b: sourceStats.b.mean},
                  stddev: isGrayscale ? sourceStats.l.stddev : {r: sourceStats.l.stddev, g: sourceStats.a.stddev, b: sourceStats.b.stddev},
              }
            : buildLiveLabStats(imageOpNodeBag, "source", labSource)

        const targetMean = isGrayscale ? targetStats.l.mean : {r: targetStats.l.mean, g: targetStats.a.mean, b: targetStats.b.mean}
        const targetStddev = isGrayscale ? targetStats.l.stddev : {r: targetStats.l.stddev, g: targetStats.a.stddev, b: targetStats.b.stddev}

        // when sourceStats was supplied, both operands are plain constants — dividing them via
        // plain JS instead of an ImageOpNodeMath node, since that node's imgproc backend (used
        // during baking) requires at least one operand to be an actual image and rejects
        // color-over-color math. When sourceStddev is a live node (the baking fallback above), this
        // is "constant over image", which ImageOpNodeMath already handles fine.
        const stddevRatio = sourceStats
            ? divideStatsOperands(targetStddev, sourceStddev as StatsOperand)
            : imageOpNodeBag.getNode("stddevRatio", ImageOpNodeMath, {
                  operator: "/safe",
                  operandA: targetStddev,
                  operandB: sourceStddev,
              })
        const centered = imageOpNodeBag.getNode("centered", ImageOpNodeMath, {
            operator: "-",
            operandA: labSource,
            operandB: sourceMean,
        })
        const rescaled = imageOpNodeBag.getNode("rescaled", ImageOpNodeMath, {
            operator: "*",
            operandA: centered,
            operandB: stddevRatio,
        })
        const labMatched = imageOpNodeBag.getNode("labMatched", ImageOpNodeMath, {
            operator: "+",
            operandA: rescaled,
            operandB: targetMean,
        })

        // Luminance controls how much of the L-channel correction is applied, Color Intensity the
        // a/b (chroma) channels'.
        const blendChannel = (id: string, channel: "R" | "G" | "B", alpha: number) => {
            const sourceChannel = imageOpNodeBag.getNode(`${id}Source`, ImageOpNodeExtractChannel, {
                sourceImage: labSource,
                channel,
            })
            const matchedChannel = imageOpNodeBag.getNode(`${id}Matched`, ImageOpNodeExtractChannel, {
                sourceImage: labMatched,
                channel,
            })
            return imageOpNodeBag.getNode(`${id}Blend`, ImageOpNodeBlend, {
                backgroundImage: sourceChannel,
                foregroundImage: matchedChannel,
                alpha,
                blendMode: "over",
                premultipliedAlpha: false,
            })
        }

        const lFinal = blendChannel("l", "R", luminance)
        let recoloredImage: ValueOrNode<AnyImageOpResult>
        if (isGrayscale) {
            recoloredImage = lFinal
        } else {
            const aFinal = blendChannel("a", "G", colorIntensity)
            const bFinal = blendChannel("b", "B", colorIntensity)
            const labBlended = imageOpNodeBag.getNode("labBlended", ImageOpNodeCombineChannels, {
                sourceImageR: lFinal,
                sourceImageG: aFinal,
                sourceImageB: bFinal,
            })

            recoloredImage = imageOpNodeBag.getNode("toRgb", ImageOpNodeConvertColorSpace, {
                sourceImage: labBlended,
                sourceColorSpace: "lab",
                targetColorSpace: "linear",
            })
        }

        // Fade blends the whole recolored result back against the untouched original
        let resultImage: ValueOrNode<AnyImageOpResult>
        if (fade === 1) {
            resultImage = recoloredImage
        } else {
            resultImage = imageOpNodeBag.getNode("fadeBlend", ImageOpNodeBlend, {
                backgroundImage: sourceImage,
                foregroundImage: recoloredImage,
                alpha: fade,
                blendMode: "over",
                premultipliedAlpha: false,
            })
        }

        const clippedResultImage = imageOpNodeBag.getNode("clippedResult", ImageOpNodeMath, {
            operator: "clip01",
            operand: resultImage,
        })

        return await evaluator.get(clippedResultImage)
    }
}

type StatsOperand = number | {r: number; g: number; b: number}

function safeDivide(a: number, b: number): number {
    return b === 0 ? 0 : a / b
}

// mirrors ImageOpNodeMath's "/safe" GLSL semantics, for the color-over-color case that node itself
// doesn't support (its imgproc backend requires at least one operand to be an image)
function divideStatsOperands(a: StatsOperand, b: StatsOperand): StatsOperand {
    if (typeof a === "number" && typeof b === "number") {
        return safeDivide(a, b)
    }
    if (typeof a === "object" && typeof b === "object") {
        return {r: safeDivide(a.r, b.r), g: safeDivide(a.g, b.g), b: safeDivide(a.b, b.b)}
    }
    throw new Error("divideStatsOperands: mismatched operand shapes")
}
